import React from 'react';

// i18n
import { resolveDirection, resolveLocale } from '../i18n';

interface DirectionBoundaryProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Puts the module's subtree under the active locale's writing direction.
 *
 * WHY A DOM ATTRIBUTE AND NOT A STYLESHEET
 * ----------------------------------------
 * `dir` is what makes the *browser* do the flipping. Logical CSS —
 * `justify-content: flex-start`, `margin-inline-start`, `text-align: start` —
 * resolves against the element's direction, so one attribute turns every
 * already-logical rule in the subtree around. The alternative, a stylesheet of
 * `[dir="rtl"] .thing { … }` overrides, is a second copy of the layout that
 * drifts from the first.
 *
 * `lang` travels with it. It is not decoration: the browser uses it for
 * hyphenation, for font fallback on CJK, for spell-check, and it is what a
 * screen reader reads to choose a voice. A page that says `dir="rtl"` without
 * saying which language is announced in the user's default voice regardless.
 *
 * WHAT DELIBERATELY DOES *NOT* FLIP
 * ---------------------------------
 * Two things in this module are physical on purpose, and a future RTL pass must
 * leave them alone:
 *
 *   1. **Photo anchors.** `top-left`, `bottom-right` and friends are named slots
 *      in the published contract (`contract/schema.ts`). An author who pins a
 *      badge to `top-left` has chosen a corner of the image, not "the corner
 *      where text begins". Flipping them would change every existing card's
 *      appearance the day a viewer's locale changed.
 *
 *   2. **Field alignment.** `alignment: 'left' | 'right'` is likewise a stored,
 *      author-chosen value in the contract, rendered with `text-left` /
 *      `text-right`. It means left.
 *
 * Everything else — the editor chrome, the pane and panel columns, the field
 * rows, the dialogs — is written with logical utilities and follows this
 * attribute.
 */
const DirectionBoundary = ({ children, className }: DirectionBoundaryProps) => {
  const locale = resolveLocale();

  return (
    <div dir={resolveDirection(locale)} lang={locale} className={className}>
      {children}
    </div>
  );
};

export default DirectionBoundary;
